import { useCallback, useState } from "react";
import { Expand } from "lucide-react";
import { useLang } from "../../lib/i18n";
import { Lightbox } from "./Lightbox";

const PHOTOS = [
  ["/images/farm-group.jpg", "g.planting"],
  ["/images/hero-pellets.jpg", "g.pellets"],
  ["/images/production-line.jpg", "g.line"],
  ["/images/farmer-harvest.jpg", "g.harvest"],
  ["/images/pellets-8mm.jpg", "g.pellets8"],
  ["/images/plant.jpg", "g.mill"],
  ["/images/farm-walk.jpg", "g.field"],
  ["/images/briquettes.jpg", "g.briquettes"],
  ["/images/farmer-plant.jpg", "g.farmer"],
  ["/images/pellets-6mm.jpg", "g.husk"],
];

const Card = ({ src, caption, index, onOpen }) => (
  <button type="button" onClick={onOpen} aria-label={caption} data-testid={`gallery-photo-${index}`} className="group relative shrink-0 w-[240px] sm:w-[300px] aspect-[4/5] overflow-hidden rounded-[24px] ring-1 ring-[#0F8A78]/30 bg-[var(--forest-2)] text-left cursor-zoom-in focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--husk)]">
    <img src={src} alt={caption} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#071a15]/85 via-transparent to-transparent" />
    <span className="absolute top-4 right-4 grid h-9 w-9 place-items-center rounded-full bg-black/40 text-[var(--cream)] opacity-0 transition-opacity group-hover:opacity-100"><Expand className="h-4 w-4" /></span>
    <span className="absolute bottom-4 left-4 right-4 font-mono text-[12px] tracking-[0.12em] uppercase text-[var(--cream)]">{caption}</span>
  </button>
);

export const GalleryStrip = () => {
  const { t } = useLang();
  const [active, setActive] = useState(null);
  const close = useCallback(() => setActive(null), []);
  const photos = PHOTOS.map(([src, key]) => ({ src, caption: t(key) }));
  const row = [...PHOTOS, ...PHOTOS];
  return (
    <section data-testid="gallery-strip" aria-label={t("g.label")} className="relative overflow-hidden border-t hairline py-16 sm:py-24">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 mb-8 sm:mb-10 flex flex-wrap items-end justify-between gap-4">
        <p className="eyebrow">{t("g.eyebrow")}</p>
        <p className="font-serif-i text-2xl sm:text-3xl text-[var(--husk)]">{t("g.tag")}</p>
      </div>
      <div className={`gallery-track flex w-max gap-4 sm:gap-6 hover:[animation-play-state:paused] ${active !== null ? "[animation-play-state:paused]" : ""}`}>
        {row.map(([src, key], i) => <Card key={i} index={i} src={src} caption={t(key)} onOpen={() => setActive(i % PHOTOS.length)} />)}
      </div>
      <Lightbox photos={photos} index={active} onClose={close} onIndex={setActive} labels={{ close: t("g.close"), prev: t("g.prev"), next: t("g.next") }} />
      <div className="pointer-events-none absolute inset-y-0 left-0 w-10 sm:w-24 bg-gradient-to-r from-[#071a15] to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-10 sm:w-24 bg-gradient-to-l from-[#071a15] to-transparent" />
    </section>
  );
};
